import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { formatPkbSourceLabel } from './productKnowledgeUtils';
import { SV } from '../../automation/stratovaiTestIds';

const ProductKnowledgeFilters = ({
  activeTab,
  selectedCount,
  onRefresh,
  onDeleteSelected,
  onMoveToStagedSelected,
  onApproveSelected,
  onRestoreSelected,
  searchTerm,
  onSearchTermChange,
  onClearSearch,
  showFilters,
  onToggleFilters,
  filters,
  onFiltersChange,
  hierarchyView,
  onHierarchyViewChange,
  itemsPerPage,
  onItemsPerPageChange,
  filterOptions,
}) => {
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Button data-testid={SV.pk.toolbarRefresh} size="small" variant="outlined" sx={{ mr: 1 }} onClick={onRefresh}>
            Refresh
          </Button>
          {activeTab === 'staged' && (
            <>
              <Button data-testid={SV.pk.toolbarDeleteSelected} size="small" variant="outlined" sx={{ mr: 1 }} disabled={!selectedCount} onClick={onDeleteSelected}>
                Delete Selected
              </Button>
              <Button data-testid={SV.pk.toolbarApproveSelected} size="small" variant="contained" disabled={!selectedCount} onClick={onApproveSelected}>
                Approve Selected
              </Button>
            </>
          )}
          {activeTab === 'approved' && (
            <>
              <Button
                data-testid={SV.pk.toolbarMoveToStaged}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
                disabled={!selectedCount}
                onClick={onMoveToStagedSelected}
              >
                Move to Staged
              </Button>
              <Button data-testid={SV.pk.toolbarDeleteSelected} size="small" variant="outlined" sx={{ mr: 1 }} disabled={!selectedCount} onClick={onDeleteSelected}>
                Delete Selected
              </Button>
            </>
          )}
          {activeTab === 'deleted' && (
            <Button data-testid={SV.pk.toolbarRestoreSelected} size="small" variant="contained" disabled={!selectedCount} onClick={onRestoreSelected}>
              Restore Selected
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search knowledge items..."
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            inputProps={{ 'data-testid': SV.pk.search }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={onClearSearch}>
                  <ClearIcon />
                </IconButton>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <Button
            data-testid={SV.pk.filtersToggle}
            size="small"
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={onToggleFilters}
            sx={{
              minWidth: 'auto',
              px: 2,
              ...(activeFilterCount > 0 && {
                '&::after': {
                  content: `"${activeFilterCount}"`,
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
              }),
            }}
          >
            Filters
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedCount > 0 && (
            <Chip
              label={`${selectedCount} selected`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </Box>

      {showFilters && (
        <Box
          data-testid={SV.pk.filterPanel}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Hierarchy</InputLabel>
                <Select
                  value={hierarchyView}
                  label="Hierarchy"
                  onChange={(event) => onHierarchyViewChange(event.target.value)}
                >
                  <MenuItem value="top_level">Top-level only</MenuItem>
                  <MenuItem value="subtasks">Subtasks only</MenuItem>
                  <MenuItem value="all">All (Top-level + Subtasks)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Source</InputLabel>
                <Select
                  value={filters.source}
                  label="Source"
                  onChange={(event) => onFiltersChange({ ...filters, source: event.target.value })}
                >
                  <MenuItem value="">All Sources</MenuItem>
                  {filterOptions.sources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {formatPkbSourceLabel(source)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Knowledge Type</InputLabel>
                <Select
                  value={filters.knowledgeType}
                  label="Knowledge Type"
                  onChange={(event) => onFiltersChange({ ...filters, knowledgeType: event.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {filterOptions.knowledgeTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Module</InputLabel>
                <Select
                  value={filters.module}
                  label="Module"
                  onChange={(event) => onFiltersChange({ ...filters, module: event.target.value })}
                >
                  <MenuItem value="">All Modules</MenuItem>
                  {filterOptions.modules?.map((moduleValue) => (
                    <MenuItem key={moduleValue} value={moduleValue}>
                      {moduleValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Items per page"
                  onChange={(event) => onItemsPerPageChange(event.target.value)}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};

export default ProductKnowledgeFilters;
